import { Picture } from "@/components/media/Picture";
import { createFileRoute } from "@tanstack/react-router";

import { Footer } from "@/components/layout/Footer";
import { Header } from "@/components/layout/Header";
import { pageBlocks } from "@/lib/content";

const TITLE = "Core Values — Light of Life International";
const DESCRIPTION =
  "Evangelism, compassion, transformation and destiny: the four core values that shape every Light of Life International outreach.";

export const Route = createFileRoute("/core-values")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://loli.ardent.dev/core-values" },
    ],
    links: [{ rel: "canonical", href: "https://loli.ardent.dev/core-values" }],
  }),
  component: CoreValuesPage,
});

function CoreValuesPage() {
  const blocks = pageBlocks("core-values");
  const text = (i: number) => {
    const block = blocks[i];
    return block && "text" in block ? block.text : "";
  };

  const values = [
    { heading: text(4), body: [text(5), text(6)] },
    { heading: text(7), body: [text(8)] },
    { heading: text(9), body: [text(10)] },
    { heading: text(11), body: [text(12), text(13)] },
  ];

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <Header overlay={false} />

      <main id="main" className="flex-1">
        {/* Navy band with rounded verse photo card */}
        <section className="relative bg-navy pt-28 pb-0">
          <div className="container-site relative">
            <h1 className="relative z-10 text-center text-5xl text-white sm:text-7xl">
              Core Values
            </h1>
            <div className="relative mt-10 overflow-hidden rounded-2xl">
              <Picture
                src="/media/home-quote-scaled.jpg"
                alt="Crowd with hands raised at a Light of Life International crusade"
                className="h-[420px] w-full object-cover sm:h-[520px]"
                sizes="(max-width: 768px) 100vw, 50vw"
                widths={[640, 1024]}
              />
              <div className="absolute inset-0 bg-navy/45" aria-hidden />
              <div className="absolute inset-x-0 top-12 mx-auto max-w-2xl px-6 text-center">
                <p className="font-heading text-xl leading-snug text-white sm:text-2xl">
                  {text(1)}
                </p>
                <p className="mt-4 font-heading text-xl text-white sm:text-2xl">{text(2)}</p>
              </div>
            </div>
          </div>
          <div className="absolute inset-x-0 bottom-0 h-40 bg-background" aria-hidden />
        </section>

        {/* Values grid */}
        <section className="container-site pb-24 pt-16">
          <h2 className="text-4xl text-navy sm:text-5xl">{text(3)}</h2>
          <div className="mt-10 grid gap-x-10 gap-y-14 lg:grid-cols-[1fr_1fr_auto]">
            <div className="grid gap-x-10 gap-y-14 sm:grid-cols-2 lg:col-span-2">
              {values.map((value) => (
                <div key={value.heading}>
                  <h3 className="border-b border-border pb-4 text-3xl uppercase text-muted-foreground">
                    {value.heading}
                  </h3>
                  <div className="mt-6 space-y-4">
                    {value.body.filter(Boolean).map((paragraph) => (
                      <p key={paragraph.slice(0, 24)} className="text-foreground">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <Picture
              src="/media/2018-06-22-1.jpg"
              alt="A child holding a teddy bear given during a Light of Life International outreach"
              loading="lazy"
              decoding="async"
              className="h-full max-h-[520px] w-full rounded-2xl object-cover lg:w-80"
              sizes="(max-width: 768px) 100vw, 50vw"
              widths={[640, 1024]}
            />
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
